// src/pages/site/TermsOfService.js
import React from 'react';
import { Link } from 'react-router-dom';
import { LegalPage, SITE } from '../../components/site/SiteLayout';

const UPDATED = '7 October 2026';
const mail = <a href={`mailto:${SITE.email}`}>{SITE.email}</a>;
const ext = (href, text) => <a href={href} target="_blank" rel="noopener noreferrer">{text}</a>;

const sections = [
  {
    id: 'agreement', title: 'Agreement to these terms',
    body: <>
      <p>
        These Terms of Service ("Terms") are an agreement between you and <strong>{SITE.company}</strong> ("we", "us", "our"),
        the operator of {SITE.name} ({SITE.url}). By creating an account or using {SITE.name}, you agree to these Terms and to
        our <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not use the service.
      </p>
      <p>If you use {SITE.name} on behalf of a business or client, you confirm that you are authorised to accept these Terms for them.</p>
    </>,
  },
  {
    id: 'service', title: 'The service',
    body: <>
      <p>
        {SITE.name} lets you create, schedule and publish content to third-party platforms that you connect, including Facebook,
        Instagram, LinkedIn, X, YouTube and Google Business Profile. Features may change as we improve the service or as
        platforms change their own rules and APIs.
      </p>
    </>,
  },
  {
    id: 'accounts', title: 'Your account',
    body: <>
      <ul>
        <li>You must be at least 16 years old and give accurate registration details.</li>
        <li>New accounts are reviewed before they are activated. We may decline a registration at our discretion.</li>
        <li>You are responsible for keeping your password secure and for all activity under your account. Tell us straight away at {mail} if you suspect unauthorised use.</li>
      </ul>
    </>,
  },
  {
    id: 'connected-platforms', title: 'Connected platforms',
    body: <>
      <p>
        When you connect a third-party account, you authorise {SITE.name} to access it and publish content to it on your behalf,
        within the permissions you grant. You must have the right to manage every account, page, channel or location you connect.
      </p>
      <p>
        Your use of each platform remains subject to that platform's own terms and policies, which you agree to follow. In particular,
        {' '}{SITE.name} uses YouTube API Services, and by connecting YouTube you agree to be bound by the{' '}
        {ext('https://www.youtube.com/t/terms', 'YouTube Terms of Service')}. Google's use of your information is described in the{' '}
        {ext('https://policies.google.com/privacy', 'Google Privacy Policy')}.
      </p>
      <p>
        We are not responsible for the platforms themselves, including outages, API changes, rate limits, content removal or
        account restrictions they apply. You can revoke {SITE.name}'s access at any time.
      </p>
    </>,
  },
  {
    id: 'your-content', title: 'Your content',
    body: <>
      <p>
        You keep ownership of all content you upload or create in {SITE.name}. You grant us a limited licence to store, process,
        copy and transmit that content only as needed to provide the service, for example to publish it to the platforms you select.
      </p>
      <p>You are solely responsible for your content and confirm that you have all the rights needed to publish it.</p>
    </>,
  },
  {
    id: 'acceptable-use', title: 'Acceptable use',
    body: <>
      <p>You must not use {SITE.name} to:</p>
      <ul>
        <li>publish anything unlawful, defamatory, harassing, hateful, sexually explicit involving minors, or that infringes someone else's rights;</li>
        <li>send spam, run fake engagement, or impersonate any person or organisation;</li>
        <li>break the terms, policies or rate limits of any connected platform;</li>
        <li>distribute malware, or try to access, probe or disrupt our systems or other users' accounts;</li>
        <li>copy, resell or reverse engineer the service, except where the law allows it.</li>
      </ul>
      <p>We may remove content or suspend accounts that break these rules.</p>
    </>,
  },
  {
    id: 'publishing', title: 'Scheduling and publishing',
    body: <>
      <p>
        We work to publish your posts at the times you schedule, but delivery depends on the third-party platforms and on
        network conditions. Posts may be delayed or fail, for example if a platform rejects the content, a connection expires, or
        an API limit is reached. {SITE.name} shows the status of each post so you can check and retry. Please review your
        content before scheduling it, because we cannot recall a post once a platform has published it.
      </p>
    </>,
  },
  {
    id: 'fees', title: 'Fees',
    body: <p>If we introduce paid plans, we will show the price and terms before you are charged, and you will only pay if you choose a paid plan.</p>,
  },
  {
    id: 'intellectual-property', title: 'Our intellectual property',
    body: <p>{SITE.name}, including its software, design and branding, belongs to us or our licensors. These Terms give you no rights to it except the right to use the service as described here. Platform names and logos belong to their owners.</p>,
  },
  {
    id: 'privacy', title: 'Privacy',
    body: <p>Our <Link to="/privacy">Privacy Policy</Link> explains how we collect, use and protect your personal information, including data from Google and other connected platforms.</p>,
  },
  {
    id: 'termination', title: 'Suspension and termination',
    body: <>
      <p>
        You can stop using {SITE.name} and delete your account at any time from your profile. We may suspend or close your account
        if you break these Terms, if required by law, or if your use creates risk for us, other users or a connected platform. Where
        reasonable, we will tell you first.
      </p>
      <p>When an account is deleted, its data is removed as described in our <Link to="/privacy#retention">Privacy Policy</Link>.</p>
    </>,
  },
  {
    id: 'disclaimers', title: 'Disclaimers',
    body: <>
      <p>
        Nothing in these Terms excludes, restricts or modifies any consumer guarantee, right or remedy you have under the
        Australian Consumer Law or any other law that cannot be excluded.
      </p>
      <p>
        Subject to that, {SITE.name} is provided "as is" and "as available". We do not promise that the service will be
        uninterrupted, error-free or that any post will achieve a particular result.
      </p>
    </>,
  },
  {
    id: 'liability', title: 'Limitation of liability',
    body: <>
      <p>To the extent permitted by law:</p>
      <ul>
        <li>we are not liable for any indirect or consequential loss, or for loss of profits, revenue, data, reputation or business opportunity;</li>
        <li>where our liability for failure to comply with a consumer guarantee cannot be excluded but can be limited, it is limited to supplying the services again or paying the cost of having them supplied again;</li>
        <li>otherwise, our total liability to you is limited to the amount you paid us for the service in the 12 months before the claim, or AUD $100 if you have paid nothing.</li>
      </ul>
    </>,
  },
  {
    id: 'indemnity', title: 'Indemnity',
    body: <p>You agree to indemnify us against claims, losses and costs arising from your content, or from your breach of these Terms or of any connected platform's terms, except to the extent caused by us.</p>,
  },
  {
    id: 'changes', title: 'Changes to these terms',
    body: <p>We may update these Terms from time to time. We will give you notice of significant changes by email or in {SITE.name} before they take effect. If you keep using the service after that, the updated Terms apply.</p>,
  },
  {
    id: 'governing-law', title: 'Governing law',
    body: <p>These Terms are governed by the laws of {SITE.jurisdiction}. You and we submit to the non-exclusive jurisdiction of the courts of Queensland and the courts that can hear appeals from them.</p>,
  },
  {
    id: 'contact', title: 'Contact us',
    body: <p><strong>{SITE.company}</strong><br />Email: {mail}<br />Website: <a href={SITE.url}>{SITE.url}</a></p>,
  },
];

export default function TermsOfService() {
  return (
    <LegalPage
      title="Terms of Service"
      updated={UPDATED}
      intro={<p>Please read these Terms carefully. They explain the rules for using {SITE.name} and your rights and responsibilities.</p>}
      sections={sections}
    />
  );
}
